import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../utils/api';
import { hasOtcDisclaimer } from '../utils/productCategory';
import {
  ReportHero,
  ReportThemeToggle,
  OriginalCopyAnalysis,
  DetailedDiagnosis,
  PrescriptionTable,
  SentenceVerdictTable,
  IgnoredUnitsPanel,
  SafeRewrites,
  MarketingTip,
  MethodologyAccordion,
  ReportFooterActions,
  YourAnswers,
  OptionalImprovements,
  KeyRulesTriggered,
  RegulatoryReferences,
  RiskAssessmentDetail,
  riskBucket,
} from '../components/report/ReportSections';
import { buildPrescriptionGroups } from '../components/report/reportData';
import OriginalInputCollapse from '../components/OriginalInputCollapse';
import { getPreviewReport } from './previewReportFixtures';
import '../styles/reportDetail.css';

function PublicHeader({ isKo }) {
  return (
    <header className="rd-public-header">
      <Link to="/" className="rd-logo" aria-label="VeriPass">VeriPass</Link>
      <Link to="/login?signup=true" className="rd-cta-btn">
        {isKo ? 'VeriPass로 무료 검증 시작하기' : 'Start free verification'}
      </Link>
    </header>
  );
}

function Skeleton() {
  return (
    <div className="rd-card">
      <div className="rd-skel rd-skel-h1" />
      <div className="rd-skel rd-skel-badge" />
      <div className="rd-skel rd-skel-meta" />
      <hr className="rd-divider" />
      <div className="rd-skel rd-skel-h2" />
      <div className="rd-skel rd-skel-line" />
      <div className="rd-skel rd-skel-line short" />
    </div>
  );
}

function ErrorScreen({ icon, title, desc, ctaLabel, ctaHref }) {
  return (
    <div className="rd-card rd-error">
      <div className="rd-error-icon">{icon}</div>
      <h1 className="rd-error-title">{title}</h1>
      <p className="rd-error-desc">{desc}</p>
      {ctaHref && (
        <Link to={ctaHref}><button type="button" className="rd-btn-primary">{ctaLabel}</button></Link>
      )}
    </div>
  );
}

export default function ReportDetailPage({ theme = 'light', setTheme }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const isLoggedIn = !!user;
  const reportTheme = theme === 'dark' ? 'dark' : 'light';
  const toggleTheme = () => {
    if (setTheme) setTheme((current) => current === 'dark' ? 'light' : 'dark');
  };
  // Clinical is the only report format. The former "detailed" view and its
  // in-session toggle were removed (CMO-REPORT-UI-CHANGE-8-12).
  const wrapperBase = `rd-page ${reportTheme === 'dark' ? 'rd-dark' : ''}`.trim();
  const isPreview = String(id || '').startsWith('_preview-');

  const [state, setState] = useState('loading'); // loading | ok | not_found | forbidden | error
  const [report, setReport] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(isLoggedIn ? '/reports' : '/');
  };

  const membershipTier = user?.membership_tier || user?.subscription?.tier;
  const isPaidPlan = membershipTier && membershipTier !== 'trial' && membershipTier !== 'free';
  const isSiteAdmin = !!(user?.is_site_admin || user?.is_root);
  const canDownloadPdf = isSiteAdmin || isPaidPlan;

  // One-time cleanup: clear the now-unused persisted format preference.
  useEffect(() => {
    window.localStorage.removeItem('vp_report_format');
  }, []);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      setState('loading');
      try {
        if (isPreview) {
          const previewBucket = String(id).replace('_preview-', '');
          const fixture = getPreviewReport(previewBucket);
          if (fixture) {
            if (!cancelled) { setReport({ ...fixture, _full: true }); setState('ok'); }
            return;
          }
        }
        if (isLoggedIn) {
          const res = await apiFetch(
            `/api/v1/reports/${encodeURIComponent(id)}?locale=${isKo ? 'ko' : 'en'}`,
            { method: 'GET' },
          );
          if (res.ok) {
            const data = await res.json();
            if (!cancelled) { setReport({ ...data, _full: true }); setState('ok'); }
            return;
          }
        }
        const pub = await apiFetch(
          `/api/v1/reports/public/${encodeURIComponent(id)}`,
          { method: 'GET', skipAuth: true },
        );
        if (pub.ok) {
          const data = await pub.json();
          if (!cancelled) { setReport({ ...data, _full: false }); setState('ok'); }
          return;
        }
        if (pub.status === 404) { if (!cancelled) setState('not_found'); return; }
        if (pub.status === 403) { if (!cancelled) setState('forbidden'); return; }
        if (!cancelled) setState('error');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [id, isLoggedIn, authLoading, isKo, isPreview]);

  const handleDownloadPdf = async () => {
    if (!canDownloadPdf || pdfLoading || isPreview) return;
    setPdfLoading(true);
    try {
      const res = await apiFetch(
        `/api/v1/reports/${encodeURIComponent(id)}/pdf?locale=${isKo ? 'ko' : 'en'}`,
        { method: 'GET' },
      );
      if (!res.ok) throw new Error('PDF failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `VERIPASS_Report_${(report?.title || report?.product_name || 'report').slice(0, 40).replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* silent */
    } finally {
      setPdfLoading(false);
    }
  };

  const showToast = () => {
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 1500);
  };

  const handleCopyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      ta.remove();
    }
    showToast();
  };

  const handleCopyLink = () => handleCopyText(window.location.href);

  const handleExportSheets = async () => {
    if (!report?._full || isPreview) return;
    try {
      const res = await apiFetch(
        `/api/v1/reports/${encodeURIComponent(id)}/csv?locale=${isKo ? 'ko' : 'en'}`,
        { method: 'GET' },
      );
      if (!res.ok) throw new Error('CSV failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `VERIPASS_Report_${(report?.title || report?.product_name || 'report').slice(0, 40).replace(/[^A-Za-z0-9_-]/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* silent */
    }
  };

  if (authLoading || state === 'loading') {
    return (
      <div className={wrapperBase}>
        {!isLoggedIn && <PublicHeader isKo={isKo} />}
        <main className="rd-main"><div style={{ padding: 32 }}><Skeleton /></div></main>
      </div>
    );
  }
  if (state === 'not_found') {
    return (
      <div className={wrapperBase}>
        {!isLoggedIn && <PublicHeader isKo={isKo} />}
        <main className="rd-main"><div style={{ padding: 32 }}>
          <ErrorScreen
            icon="🔍"
            title={isKo ? '리포트를 찾을 수 없습니다' : 'Report not found'}
            desc={isKo
              ? '링크가 만료되었거나 삭제된 리포트입니다.'
              : 'This link has expired or the report was deleted.'}
            ctaLabel={isKo ? '리포트 목록으로' : 'Back to reports'}
            ctaHref={isLoggedIn ? '/reports' : '/'}
          />
        </div></main>
      </div>
    );
  }
  if (state === 'forbidden') {
    return (
      <div className={wrapperBase}>
        {!isLoggedIn && <PublicHeader isKo={isKo} />}
        <main className="rd-main"><div style={{ padding: 32 }}>
          <ErrorScreen
            icon="🔒"
            title={isKo ? '접근 권한이 없습니다' : 'Access denied'}
            desc={isKo
              ? '이 리포트를 보려면 해당 계정으로 로그인하세요.'
              : 'Sign in with the account that owns this report.'}
            ctaLabel={isKo ? '로그인하기' : 'Sign in'}
            ctaHref="/login"
          />
        </div></main>
      </div>
    );
  }
  if (state === 'error' || !report) {
    return (
      <div className={wrapperBase}>
        {!isLoggedIn && <PublicHeader isKo={isKo} />}
        <main className="rd-main"><div style={{ padding: 32 }}>
          <ErrorScreen
            icon="⚠️"
            title={isKo ? '일시적인 오류가 발생했습니다' : 'Something went wrong'}
            desc={isKo
              ? '잠시 후 다시 시도해주세요.'
              : 'Please try again in a moment.'}
            ctaLabel={isKo ? '다시 시도' : 'Retry'}
            ctaHref={`/reports/${id}`}
          />
        </div></main>
      </div>
    );
  }

  // ── Normalize fields between owner-full vs public-redacted shapes ──
  const isFull = !!report._full;
  const ar = isFull ? (report.analysis_result || {}) : null;
  // Structured outputs live under agent_metadata (recommendations, citations,
  // risk_assessment). Reading top-level `ar.*` left the clinical view "barren".
  const meta = isFull ? (ar.agent_metadata || {}) : {};
  // Canonical English enums drive ALL logic (severity, labels). Localized strings
  // are never fed to riskBucket/classificationLabel — that caused the green-vs-severe
  // disparity between the clinical and detailed views.
  const classificationEn = isFull
    ? (ar.classification_en || ar.classification || 'COSMETIC')
    : (report.classification_en || report.classification || 'COSMETIC');
  const riskLevelEn = isFull
    ? (ar.risk_level_en || ar.risk_level || '')
    : (report.risk_level_en || report.risk_level || '');
  const confidence = isFull
    ? (ar.confidence != null ? ar.confidence : null)
    : (report.confidence != null ? report.confidence : null);
  // T3.1c — the Korean (기능성화장품) reading. Present on both the owner
  // and the share-link payload; null whenever KR_JURISDICTION_ENABLED is off or
  // the report predates it, in which case the hero renders nothing extra.
  const krJurisdiction = isFull
    ? (ar.kr_jurisdiction || null)
    : (report.kr_jurisdiction || null);
  // Pass confidence so riskBucket can apply the low-confidence-never-green
  // defense in depth (D1/D3 of validation-error-fix-7-13.md) — `null` is
  // treated the same as "unknown" (no forcing), consistent with existing callers.
  const bucket = riskBucket(riskLevelEn, classificationEn, confidence);
  const originalText = isFull
    ? (report.input_text || report.original_input || report.text || '')
    : (report.input_text || '');
  const ruleHits = isFull ? (ar.rule_hits || []) : (report.rule_hits || []);
  const highHitCount = ruleHits.filter((h) =>
    ['HIGH', 'CRITICAL'].includes(String(h.severity || h.risk_level || '').toUpperCase())
  ).length;
  const hitCount = bucket === 'high'
    ? (highHitCount || ruleHits.length)
    : ruleHits.length;

  const showOtcDisclaimer = hasOtcDisclaimer(report, isFull);

  // Prescription (권장 대안): ALL flagged phrases with a term-specific safe
  // alternative, grouped into ONE CARD PER FLAGGED SENTENCE (Phase 2). See
  // buildPrescriptionGroups for the grouping + legacy fallback.
  const prescriptionGroups = buildPrescriptionGroups(ruleHits);

  // Recommendations (live under agent_metadata; fall back to top-level). Still
  // used by OptionalImprovements on low-risk reports. The "Recommended Claims"
  // card was removed — it rendered fabricated safety/match percentages with no
  // backing logic; it will return once the Marketing-AI/LLM completion lands.
  const recs = isFull
    ? ((meta.recommendations && meta.recommendations.length ? meta.recommendations : ar.recommendations) || [])
    : (report.recommendations || []);

  // Methodology data — citations live under agent_metadata; derive from rule
  // hits only as a last resort. (Empty arrays are truthy, so check length.)
  const citations = isFull
    ? ((meta.citations && meta.citations.length) ? meta.citations
       : (ar.citations && ar.citations.length) ? ar.citations
       : ruleHits.map((h) => h.citation).filter(Boolean).map((c) =>
            typeof c === 'string' ? { code: c } : c))
    : [];
  const riskAssessment = isFull ? (meta.risk_assessment || '') : '';
  // similar_cases is a FLAT list on the real AnalysisResult wire shape (each row
  // carries its own `type`: 'drug_precedent' | 'cosmetic_safe' | 'otc_monograph'),
  // not an object keyed by drug/cosmetic — bucket it here instead of trusting a
  // shape (`ar.similar_cases?.drug`, `ar.similar_drug_matches`) that never exists
  // on the backend model (schemas.py AnalysisResult.similar_cases). Reading the
  // wrong shape silently produced an always-empty list, which is part of why the
  // "선례 없음" empty state rendered even when the corpus had a real match (T5.3).
  const rawSimilarCases = isFull && Array.isArray(ar.similar_cases) ? ar.similar_cases : [];
  const similarCases = isFull ? {
    drug: rawSimilarCases.filter((c) => c && c.type === 'drug_precedent'),
    cosmetic: rawSimilarCases.filter((c) => c && c.type === 'cosmetic_safe'),
  } : { drug: [], cosmetic: [] };
  // T5.3: an empty similar_cases list is ambiguous — "the lookup ran and found
  // nothing" vs. "the lookup could not run" (failed embedding / vector retrieval
  // outage). AnalysisResult.degraded_reasons already distinguishes these with
  // stable reason codes (analysis_service.py) — use it instead of guessing.
  const degradedReasons = isFull && Array.isArray(ar?.degraded_reasons) ? ar.degraded_reasons : [];
  const retrievalUnavailable =
    degradedReasons.includes('vector_retrieval_failed') || degradedReasons.includes('embedding_failed');
  const scoring = isFull ? {
    rule_engine: ar.rule_engine_confidence ?? ar.scoring_breakdown?.rule_engine ?? null,
    vector: ar.vector_confidence ?? ar.scoring_breakdown?.vector ?? null,
  } : null;

  const topCitation = ruleHits.find((h) => h.citation)?.citation || null;
  const wrapperClass = wrapperBase;

  // ── CU (Compute Unit) metering surfaces — §8 P5 T5.1/T5.2/T5.6 ──
  // cu_metering_enabled is a server-side flag the frontend has no access to;
  // every one of these gates keys off the DATA actually present on this
  // report, never a guessed flag. With CU metering off (the default in prod
  // today), analysis_result.units/ignored_units/safe_rewrites are null and
  // none of the sections below render — the rest of the report is untouched.
  const units = isFull && Array.isArray(ar?.units) ? ar.units : null;
  const ignoredUnits = isFull && Array.isArray(ar?.ignored_units) ? ar.ignored_units : null;
  const safeRewrites = isFull && Array.isArray(ar?.safe_rewrites) ? ar.safe_rewrites : null;
  const hasUnitBreakdown = !!(units && units.length > 0);

  return (
    <div className={wrapperClass}>
      {!isLoggedIn && setTheme && (
        <div className="rd-page-theme-control rd-page-theme-control--public">
          <ReportThemeToggle theme={reportTheme} onToggle={toggleTheme} />
        </div>
      )}

      {!isLoggedIn && <PublicHeader isKo={isKo} />}

      <main className="rd-main">
        {isLoggedIn && (
          <button type="button" className="rd-back-btn" onClick={handleBack}>
            ← {isKo ? '리포트 목록으로' : 'Back to reports'}
          </button>
        )}
        <ReportHero
          report={report}
          confidence={confidence}
          riskBucketKey={bucket}
          riskLevelEn={riskLevelEn}
          classification={classificationEn}
          hitCount={hitCount}
          krJurisdiction={krJurisdiction}
        />

        {/* Section order follows the CMO clinical-report mockup
            (CMO-REPORT-UI-CHANGE-8-12 · B3):
            문구 분석 → 권장 대안 → 마케팅 팁 → 리스크 유형 → 리스크 상세
            → 분석 방법(열림) → 규제 규칙(접힘) → 규제 근거(접힘). */}
        <div className="rd-body-wrap">
          {showOtcDisclaimer && (
            <div role="alert" className="rd-otc-disclaimer">
              {t('analyze.otcDisclaimer')}
            </div>
          )}

          {/* 01 문구 분석 */}
          <OriginalCopyAnalysis
            originalText={originalText}
            ruleHits={ruleHits}
            classification={classificationEn}
            riskBucketKey={bucket}
          />

          <YourAnswers answers={ar?.questionnaire_answers} />

          {/* 02 권장 대안 — placed directly under 문구 분석 */}
          {bucket !== 'low' && (
            <PrescriptionTable groups={prescriptionGroups} onCopy={handleCopyText} />
          )}

          {/* Rewrite suggestions belong with 권장 대안 (mutually exclusive:
              OptionalImprovements on a clean report, SafeRewrites otherwise). */}
          {bucket === 'low' ? (
            <OptionalImprovements
              suggestions={
                (ar?.optional_improvements && ar.optional_improvements.length
                  ? ar.optional_improvements
                  : (recs || []).slice(0, 2))
              }
              onSelect={handleCopyText}
            />
          ) : (
            safeRewrites && safeRewrites.length > 0 && (
              <SafeRewrites rewrites={safeRewrites} onCopy={handleCopyText} />
            )
          )}

          {/* 03 마케팅 팁 — moved up per CMO */}
          <MarketingTip bucket={bucket} />

          {/* 04 리스크 유형 */}
          {bucket !== 'low' && (
            <DetailedDiagnosis
              riskBucketKey={bucket}
              classification={classificationEn}
              citation={topCitation}
            />
          )}

          {/* 05 리스크 상세 — stacked directly under 리스크 유형 */}
          {bucket !== 'low' && riskAssessment && (
            <RiskAssessmentDetail text={riskAssessment} />
          )}

          {/* §8 P5 T5.1/T5.2/T5.4 — per-sentence CU ledger, ignored/
              non-cosmetics list, raw-input collapse. Analysis detail, so it
              sits just above 분석 방법. Gated on `units` (independent of
              `bucket`); no-op for legacy (non-CU) reports. */}
          {hasUnitBreakdown && (
            <SentenceVerdictTable units={units} />
          )}

          {ignoredUnits && ignoredUnits.length > 0 && (
            <IgnoredUnitsPanel ignoredUnits={ignoredUnits} />
          )}

          {hasUnitBreakdown && (
            <OriginalInputCollapse originalText={originalText} />
          )}

          {/* 06 분석 방법 — expanded by default */}
          <MethodologyAccordion
            citations={citations}
            similarCases={similarCases}
            scoring={scoring}
            finalConfidence={confidence}
            riskBucketKey={bucket}
            retrievalUnavailable={retrievalUnavailable}
            defaultOpen
          />

          {/* 07 규제 규칙 — collapsed by default */}
          <KeyRulesTriggered ruleHits={ruleHits} collapsible />

          {/* 08 규제 근거 — collapsed by default */}
          <RegulatoryReferences citations={citations} collapsible />
        </div>

        <ReportFooterActions
          showPdf={isLoggedIn && isFull && !isPreview}
          pdfLoading={pdfLoading}
          canDownloadPdf={canDownloadPdf}
          onDownloadPdf={handleDownloadPdf}
          onSheets={isLoggedIn && isFull && !isPreview && canDownloadPdf ? handleExportSheets : undefined}
          onShare={handleCopyLink}
          feedback={feedback}
          onFeedback={setFeedback}
        />

        {/* Bottom CTA banner — public view only */}
        {!isLoggedIn && (
          <div className="rd-cta-banner">
            <div className="rd-cta-text">
              {isKo
                ? '더 많은 제품을 검증하려면 VeriPass를 시작하세요.'
                : 'Verify more products with VeriPass.'}
            </div>
            <Link to="/login?signup=true">
              <button type="button" className="rd-btn-primary">
                {isKo ? '무료로 시작하기' : 'Get started free'}
              </button>
            </Link>
          </div>
        )}
      </main>

      {copyToast && (
        <div className="rd-toast" role="status">
          {t('report.sharedToast')}
        </div>
      )}
    </div>
  );
}
